import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Clock, AlertTriangle, Infinity as InfinityIcon } from "lucide-react";

// Displays a live countdown: days/hours/minutes above 24 hours,
// then hours/minutes/seconds for the final 24 hours.
export function ExpiryBadge({ className = "" }) {
  const { expiresAt } = useAuth();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!expiresAt) return undefined;

    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt]);

  if (!expiresAt) {
    return (
      <span
        data-testid="expiry-badge"
        title="Masa aktif permanen"
        className={`inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/15 dark:text-emerald-300 ${className}`}
      >
        <InfinityIcon className="h-3.5 w-3.5" />
        Permanen
      </span>
    );
  }

  const ms = new Date(expiresAt).getTime() - now;
  const expired = !Number.isFinite(ms) || ms <= 0;
  const totalSeconds = expired ? 0 : Math.floor(ms / 1000);

  let label;
  if (expired) {
    label = "Masa aktif habis";
  } else if (totalSeconds >= 24 * 60 * 60) {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    label = `Sisa ${days} hari ${hours} jam ${minutes} menit`;
  } else {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    label = `Sisa ${hours} jam ${minutes} menit ${seconds} detik`;
  }

  const tone = expired
    ? "border-red-600/60 bg-red-600/10 text-red-800 dark:border-red-400/60 dark:bg-red-400/15 dark:text-red-300"
    : "border-red-500/55 bg-red-500/10 text-red-700 dark:border-red-400/50 dark:bg-red-400/15 dark:text-red-300";

  return (
    <span
      data-testid="expiry-badge"
      title={`Berlaku sampai ${new Date(expiresAt).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}`}
      aria-live="off"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${tone} ${className}`}
    >
      {expired ? <AlertTriangle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}
